/**
 * The location screen before Home. Owner's decision (11 Oct 2026): location is asked for, never
 * required, so the wallet always works. When the phone has a location the app opens straight
 * away; otherwise this screen says what is wrong and offers:
 * - Turn on location: opens the phone's Location settings (Android Chrome), then checks again
 *   when the office boy comes back;
 * - Retry;
 * - Continue without location: the wallet opens; entries are saved without a place.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { useT } from '../i18n/index.ts';
import { getFix, type FixProblem } from '../lib/location.ts';
import { Icon } from '../ui/icons.tsx';
import { Spinner } from '../ui/parts.tsx';

type State = { kind: 'checking' } | { kind: 'ok' } | { kind: 'problem'; problem: FixProblem };

/** Android Chrome opens the system Location settings from this link (a tap is required). */
const ANDROID_LOCATION_SETTINGS = 'intent://#Intent;action=android.settings.LOCATION_SOURCE_SETTINGS;end';

const isAndroid = () => typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);

/** Problems the phone's Location switch can fix (not a refused permission or an http:// page). */
const SWITCH_FIXES: readonly FixProblem[] = ['off', 'timeout', 'weak'];

export function LocationGate({ children }: { children: ReactNode }) {
  const { t } = useT();
  const [state, setState] = useState<State>({ kind: 'checking' });
  const [skipped, setSkipped] = useState(false);
  const check = useCallback(() => {
    setState({ kind: 'checking' });
    void getFix().then((r) => setState(r.ok ? { kind: 'ok' } : { kind: 'problem', problem: r.problem }));
  }, []);
  useEffect(check, [check]);

  // Back from the phone's settings: check again by itself.
  const waiting = state.kind === 'problem' && !skipped;
  useEffect(() => {
    if (!waiting) return;
    const onBack = () => document.visibilityState === 'visible' && check();
    document.addEventListener('visibilitychange', onBack);
    return () => document.removeEventListener('visibilitychange', onBack);
  }, [waiting, check]);

  if (state.kind === 'ok' || skipped) return <>{children}</>;
  const problem = state.kind === 'problem' ? state.problem : null;
  const canOpenSettings = problem !== null && SWITCH_FIXES.includes(problem) && isAndroid();
  return (
    <div className="safe-top safe-bottom mx-auto flex min-h-app w-full max-w-md flex-col items-center justify-center gap-5 bg-paper px-6 py-6 text-center short:gap-3">
      <span
        className={`flex size-20 items-center justify-center rounded-full ${problem ? 'bg-warning-tint text-warning' : 'bg-brand-tint text-brand'}`}
      >
        <Icon name="pin" size={38} />
      </span>
      <div className="flex flex-col gap-2" role={problem ? 'alert' : 'status'}>
        <h1 className="m-0 text-2xl font-bold rtl:text-[26px]">{problem ? t(`gps.${problem}Title`) : t('gps.checking')}</h1>
        <p className="m-0 text-[17px] leading-relaxed text-muted rtl:text-[19px]">{problem ? t(`gps.${problem}Body`) : t('gps.why')}</p>
      </div>
      {!problem && <Spinner size={36} label={t('gps.checking')} />}
      {problem && (
        <div className="flex w-full flex-col gap-2.5">
          {canOpenSettings && (
            <a
              href={ANDROID_LOCATION_SETTINGS}
              className="flex h-14 w-full items-center justify-center gap-2 rounded-[14px] bg-brand text-lg font-bold text-white no-underline hover:bg-brand-dark rtl:text-xl"
            >
              <Icon name="pin" size={20} />
              {t('gps.turnOn')}
            </a>
          )}
          <button
            type="button"
            onClick={check}
            className={`flex h-14 w-full items-center justify-center gap-2 rounded-[14px] text-lg font-bold rtl:text-xl ${
              canOpenSettings
                ? 'border-[1.5px] border-line-strong bg-paper text-ink hover:bg-ground'
                : 'bg-brand text-white hover:bg-brand-dark'
            }`}
          >
            <Icon name="sync" size={20} />
            {t('gps.retry')}
          </button>
          {/* The wallet always works: location is asked for, never required. */}
          <button
            type="button"
            onClick={() => setSkipped(true)}
            className="flex h-12 w-full items-center justify-center text-base font-bold text-muted underline underline-offset-4 hover:text-ink rtl:text-lg"
          >
            {t('gps.skip')}
          </button>
          <p className="m-0 text-sm text-muted rtl:text-base">{t('gps.skipNote')}</p>
        </div>
      )}
    </div>
  );
}
